import { useTripSelectionCalendarTabState } from 'Modules/MultiStopWizard/Hooks/useTripSelectionCalendarTabState';
import React, { useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { WizardStepHeader } from '../Shared/WizardStepHeader';

import { useLocationById } from 'Global/JourneyElements/Hooks/useLocationById';
import { journeyElementsStore } from 'Global/JourneyElements/JourneyElementsStore';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import usePassengerTypes from 'Hooks/usePassengerTypes';
import useStore from 'Hooks/useStore';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { wizardModeOptions } from 'Models/Enums';
import { appConfigStore } from 'Models/AppConfigStore';
import { formatTripFilterInfo } from 'Util/_HumanWritten/BookingWizardDataUtils';
import { SelectedTrips } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardWrap';
import { DepartureTripSection } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Tickets/DepartureTripSection';
import { useTripClassPrices } from './useTripClassPrices';
import { useTripPricing } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Tickets/Hooks/useTripPricing';
import {
	useDepartureTripsQuery,
	useReturnTripsQuery,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Tickets/Hooks/useTripsQuery';
import { useTripValidation } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Tickets/Hooks/useTripValidation';
import BookingWizardNoticeBanner from 'Views/Components/_HumanWritten/NoticeBanner/BookingWizardNoticeBanner';

import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import {
	BookingWizardData,
	passengerSeatingSignature,
	patchAllPassengers,
} from '../../BookingWizardData/BaseBookingWizardData';
import { heldClassId } from './TripFareColumns';
import { VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { getWizardDataPassengerTicketDependencies, passengerClassPatch } from '../../BookingWizardData/PassengerDetail';

import { Button, Colors, Display } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';
import { ReturnTripSection } from './ReturnTripSection';
import { trackSearchTrips } from 'Util/_HumanWritten/Analytics/analyticsEvents';
import {
	analyticsBookingType,
	analyticsRouteLabel,
	analyticsSailingDate,
} from 'Util/_HumanWritten/Analytics/analyticsBookingFields';
import { funnelEventAlreadyTracked, markFunnelEventTracked } from 'Util/_HumanWritten/Analytics/funnelDedupe';

export interface TicketSelectionTabProps {
	selectedTrips: SelectedTrips;
}

export function TicketSelectionTab({ selectedTrips }: TicketSelectionTabProps) {
	const navigate = useNavigate();
	const { isMobileBookingWizard } = useDeviceDetect();
	const { isStaff, can } = useStore();

	const { wizardData, setWizardData, oldWizardData } = useBookingWizardDataStore();
	const startLocation = useLocationById(wizardData.fromLocationId);
	const endLocation = useLocationById(wizardData.toLocationId);

	// Fire `view_item_list` once when the customer reaches the trip-selection step.
	//
	// The ref covers a repeat within this mount; `funnelDedupe` covers navigating back to this
	// step later in the same checkout, which remounts it and resets the ref.
	const searchTrackedRef = useRef(false);
	useEffect(() => {
		if (searchTrackedRef.current || funnelEventAlreadyTracked('view_item_list')) {
			return;
		}
		searchTrackedRef.current = true;
		markFunnelEventTracked('view_item_list');
		trackSearchTrips({
			booking_type: analyticsBookingType(wizardData.tabSelected),
			route: analyticsRouteLabel(wizardData.fromLocationId, wizardData.toLocationId),
			sailing_date: analyticsSailingDate(wizardData.startDate),
			from_location_id: wizardData.fromLocationId,
			to_location_id: wizardData.toLocationId,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const isJourneyElementsLoading = journeyElementsStore(x => x.isLoading);

	/*
		Only for the re-render. `passengerTypeStore` is loaded once at start-up and is not reactive —
		App.tsx says so where it loads it — so a component that reads it directly and renders first
		reads an empty list and is never told otherwise. Every helper that walks the booking's
		passengers walks that list, including the one below that hands out the default fare type: with
		no types loaded it visits nobody, concludes nobody is missing a fare type, and never runs
		again, because none of its dependencies move afterwards.

		That is a race, not a certainty, and it is lost exactly where it is most visible — a cold load
		straight onto this step with a sailing already chosen, which is what `Book now` on the ferry
		schedule does. The hook re-renders this component when the list arrives; the effect's own
		dependency on its length is what makes it try again.
	*/
	usePassengerTypes();
	const passengerTypeCount = passengerTypeStore.passengerTypes.length;

	const onlyAlteringReturn = wizardData.wizardMode === 'ALTERATION' && wizardData.departureTrip === false;
	const passengerTicketDependencies = useMemo(
		() => getWizardDataPassengerTicketDependencies(wizardData),
		[wizardData],
	);

	// Fetch departure trips using React Query
	// When only altering the return leg, use endDate so the departure query
	// (whose results are displayed by DepartureTripSection) refetches on date changes.
	// In alteration mode the booking's existing trip is already known via
	// departureTicketId / returningTicketId — pass it through so each query can render the
	// existing card immediately while the multi-day window fetch runs in the background.
	const isAlteration = wizardData.wizardMode === 'ALTERATION' && !!wizardData.bookingToEdit;
	const initialDepartureTripId = isAlteration ? (wizardData.departureTicketId ?? null) : null;
	const initialReturnTripId = isAlteration ? (wizardData.returningTicketId ?? null) : null;

	const departureTripsQuery = useDepartureTripsQuery({
		fromLocationId: wizardData.fromLocationId,
		toLocationId: wizardData.toLocationId,
		startDate: onlyAlteringReturn ? wizardData.endDate : wizardData.startDate,
		ticketSelectionStartDate: onlyAlteringReturn
			? wizardData.ticketSelectionEndDate
			: wizardData.ticketSelectionStartDate,
		onlyAlteringReturn,
		bookingToEdit: wizardData.bookingToEdit ?? null,
		userId: wizardData.userId,
		isStaff,
		enabled: !isJourneyElementsLoading,
		initialTripId: initialDepartureTripId,
	});

	// Fetch return trips using React Query
	const returnTripsQuery = useReturnTripsQuery({
		fromLocationId: wizardData.fromLocationId,
		toLocationId: wizardData.toLocationId,
		endDate: wizardData.endDate,
		ticketSelectionEndDate: wizardData.ticketSelectionEndDate,
		onlyAlteringReturn,
		bookingToEdit: wizardData.bookingToEdit ?? null,
		userId: wizardData.userId,
		isStaff,
		enabled: !isJourneyElementsLoading && wizardData.tripType === 'return',
		initialTripId: initialReturnTripId,
	});

	// Map React Query state to the old structure for compatibility
	const departureTripsLoading = departureTripsQuery.isLoading || departureTripsQuery.isFetching;
	const destinationTripsLoading = returnTripsQuery.isLoading || returnTripsQuery.isFetching;
	const trips = {
		departureTickets: departureTripsQuery.data ?? null,
		returningTickets: returnTripsQuery.data ?? null,
	};

	// Per-class fares for every sailing on screen, departing and returning, in one request. The
	// card's headline is a single "from" figure, which on a vessel with classes does not tell a
	// booker what Business costs on each sailing.
	const { prices: classPricesByFerryTrip, isLoading: classPricesLoading } = useTripClassPrices(
		React.useMemo(
			() =>
				[...(trips.departureTickets ?? []), ...(trips.returningTickets ?? [])].map(trip => ({
					tripId: trip.id,
					startStopId: trip.startStopId,
					endStopId: trip.endStopId,
				})),
			[trips.departureTickets, trips.returningTickets],
		),
	);

	/*
		Whether each leg's layout is still undecided.

		The class-price query only starts once there are sailings to price, so while a leg's sailings
		are on their way it reports "not loading" — and an empty fare map reads as "no fare types",
		which drew the old day tabs until the sailings and their fares landed and it redrew as the fare
		rail. That happened on the first load and again every time the window moved, since a new
		window is a new query with no data. `data == null` rather than the loading flags, because the
		trips query is disabled (neither loading nor loaded) while the journey elements load. An error
		releases it so a failed fetch does not spin forever.
	*/
	const holdForFareLayout = appConfigStore.passengerClassesEnabled && wizardData.tabSelected !== 'freight';
	const departureLayoutPending =
		classPricesLoading || (holdForFareLayout && departureTripsQuery.data == null && !departureTripsQuery.isError);
	const returnLayoutPending =
		classPricesLoading || (holdForFareLayout && returnTripsQuery.data == null && !returnTripsQuery.isError);

	/**
	 * Everyone starts in the vessel's default class as soon as a sailing is chosen.
	 *
	 * <p>Choosing a class on a fare cell already does this. What did not was arriving with a sailing
	 * already selected — clicking Book on the ferry schedule, or reopening a booking — which set the
	 * trip and nothing else, so the card reported that no class had been chosen on a vessel that has
	 * a perfectly good default for exactly this case.</p>
	 *
	 * <p>Only when NOBODY has a class. A booker who has chosen one, or split the party across
	 * several, must not have it overwritten by a step they have already been through.</p>
	 */
	useEffect(() => {
		if (!wizardData) {
			return;
		}

		// Each leg on its own: they are separate sailings, possibly different vessels, with their own
		// fare types and their own defaults.
		const legs = [
			{
				leg: 'departure' as const,
				tripId: wizardData.departingJourney?.tripId ?? wizardData.departureTicketId,
			},
			{
				leg: 'return' as const,
				tripId: wizardData.returningJourney?.tripId ?? wizardData.returningTicketId,
			},
		];

		let next = wizardData;
		let changed = false;

		legs.forEach(({ leg, tripId }) => {
			const classes = tripId ? (classPricesByFerryTrip[tripId] ?? []) : [];
			if (classes.length === 0) {
				return;
			}

			/*
				What a passenger with no fare type gets.

				The party's own, where they agree on one. A booker who put everybody in Premium and then
				added a fourth passenger has answered this question already — dropping the new one into
				the vessel's default instead left the card reporting "Mixed fare types" over a choice
				nobody made, and quietly priced one passenger differently from the rest.

				Otherwise the vessel's default, else the first fare type it offers: a vessel with no
				default is a valid configuration, and leaving someone unassigned is worse than the
				cheapest.
			*/
			// Asked of the passengers who HAVE a fare type, not of the whole party: the newcomer is
			// one of the party by the time this runs, and counting their blank made every party
			// read as mixed exactly when the question was being asked.
			const partys = heldClassId(next, leg);
			const inherited = partys ? classes.find(x => x.passengerClassId === partys) : undefined;
			const fallback = inherited ?? classes.find(x => x.isDefault) ?? classes[0];

			// Is there anyone to fill in? Asked before patching, because `patchAllPassengers` always
			// returns a fresh object — comparing its result would report a change on every render and
			// write the wizard data back for nothing.
			let anyMissing = false;
			patchAllPassengers(next, passenger => {
				if (!(leg === 'return' ? passenger.returningPassengerClassId : passenger.passengerClassId)) {
					anyMissing = true;
				}
				return null;
			});

			if (!anyMissing) {
				return;
			}

			// Only the passengers who have none. Anyone who has chosen — including a party deliberately
			// split across fare types — is left exactly as they are.
			next = patchAllPassengers(next, passenger => {
				const existing = leg === 'return' ? passenger.returningPassengerClassId : passenger.passengerClassId;
				if (existing) {
					return null;
				}
				return passengerClassPatch(leg, fallback.passengerClassId, fallback.name);
			});
			changed = true;
		});

		if (changed) {
			setWizardData(next);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
		// The signature moves when a passenger is added or removed, which is the moment a new traveller
		// needs a fare type — the trip ids alone never change on that, so without it the newcomer sat
		// without one until something else re-rendered the step.
	}, [
		wizardData?.departingJourney?.tripId,
		wizardData?.departureTicketId,
		wizardData?.returningJourney?.tripId,
		wizardData?.returningTicketId,
		passengerSeatingSignature(wizardData),
		classPricesByFerryTrip,
		// See `usePassengerTypes` above: until these land there is nobody to give a fare type to.
		passengerTypeCount,
	]);

	// Fetch prices
	const { priceByDepartureFerryTrip, priceByReturnFerryTrip, departurePriceLoading, destinationPriceLoading } =
		useTripPricing(
			trips.departureTickets,
			trips.returningTickets,
			departureTripsLoading,
			destinationTripsLoading,
			wizardData,
			passengerTicketDependencies,
			initialDepartureTripId,
			initialReturnTripId,
		);

	// Validate selected trips
	useTripValidation({
		wizardData,
		setWizardData,
		trips,
		departureTripsLoading,
		destinationTripsLoading,
		oldWizardData,
		canOverbook: can('OverbookInBookingWizard'),
	});

	// Clear vehicle data when switching from vehicle to passenger
	useEffect(() => {
		const vehicleTrip = wizardData.tabSelected === 'vehicle';
		if (!vehicleTrip) {
			const newData = new BookingWizardData({ ...wizardData });
			if (newData.innerWizardData instanceof VehicleBookingWizardData) {
				newData.innerWizardData.cargoTypeId = '';
				newData.innerWizardData.cargoMake = '';
				newData.innerWizardData.vehicleLengthId = '';
				newData.innerWizardData.vehicleWidthId = '';
				newData.innerWizardData.trailerTypeId = 'NO_TRAILER';
				newData.innerWizardData.trailerLengthId = '';
				newData.innerWizardData.departingTripOptions = [];
				newData.innerWizardData.returningTripOptions = [];
			}
			setWizardData(newData);
		}
	}, [wizardData.tabSelected]);

	const selectedDepartingTrip = trips.departureTickets?.find(x => x.id === wizardData.departureTicketId);
	const selectedReturningTrip = trips.returningTickets?.find(x => x.id === wizardData.returningTicketId);

	// Back-fill the journey when only a trip id arrived via URL params (e.g. the
	// waitlist "Book Now" email link sets ferryTripId → departureTicketId). The
	// trip card renders as selected off the ticket id, but Continue validates the
	// journey object, so without this the user had to unselect and re-select.
	if (selectedDepartingTrip && !wizardData.departingJourney) {
		wizardData.departingJourney = {
			tripId: selectedDepartingTrip.id,
			startStopId: selectedDepartingTrip.startStopId,
			endStopId: selectedDepartingTrip.endStopId,
		};
	}
	if (selectedReturningTrip && !wizardData.returningJourney) {
		wizardData.returningJourney = {
			tripId: selectedReturningTrip.id,
			startStopId: selectedReturningTrip.startStopId,
			endStopId: selectedReturningTrip.endStopId,
		};
	}

	// Update journey departure times
	if (selectedDepartingTrip && wizardData.departingJourney) {
		wizardData.departingJourney.departureDateTime = selectedDepartingTrip.departureDateTime;
	}
	if (selectedReturningTrip && wizardData.returningJourney) {
		wizardData.returningJourney.departureDateTime = selectedReturningTrip.departureDateTime;
	}

	const { departingCurrentTab, returningCurrentTab, setDepartingCurrentTab, setReturningCurrentTab } =
		useTripSelectionCalendarTabState(
			wizardData.startDate,
			wizardData.endDate,
			selectedDepartingTrip?.departureDateTime,
			selectedReturningTrip?.departureDateTime,
			wizardData.departureTicketId,
			wizardData.returningTicketId,
			wizardData.ticketSelectionStartDate,
			wizardData.ticketSelectionEndDate,
		);

	const handleRemoveReturnTrip = () => {
		const newData = new BookingWizardData({ ...wizardData });
		newData.tripType = 'one way';
		newData.returningTicketId = '';
		newData.returningJourney = undefined;
		setWizardData(newData);
	};

	return (
		<div className="booking-wizard-tab ticket-selection-tab-container">
			<WizardStepHeader
				iconName="confirmation_number"
				title={renderHeader(wizardData)}
				subtitle={
					<FormattedMessage
						id="booking-wizard__tickets__subtitle"
						defaultMessage="Pick the ferry trips for your journey."
					/>
				}
				showCloseLink={wizardData.wizardMode === wizardModeOptions.ALTERATION}
				bookingToEditId={wizardData?.bookingToEdit ?? ''}
				className="ticket-tab-text"
			/>

			{/*
			 * Route notices live on this step alone: it is where the customer is still choosing sailings,
			 * so a disruption can change what they pick. Below the header rather than above it, so the
			 * step still opens with its own title.
			 */}
			<BookingWizardNoticeBanner
				selectedTrips={selectedTrips}
				// So a route notice is up before the customer commits to a sailing, not after.
				fromLocationId={wizardData.fromLocationId}
				toLocationId={wizardData.toLocationId}
				includeReturnRoute={wizardData.tripType === 'return'}
				browsingStartDate={wizardData.ticketSelectionStartDate}
				browsingEndDate={wizardData.ticketSelectionEndDate}
			/>

			<If condition={isMobileBookingWizard && wizardData.tabSelected !== 'freight'}>
				<div className="ticket-tab__info inline-button-container ticket-tab-text">
					<p className="ticket-tab__info__text">For {formatTripFilterInfo(wizardData)}</p>
					<Button
						className="edit-filter-button"
						colors={Colors.Alternate}
						display={Display.Text}
						onClick={() => navigate('/booking-wizard/search')}
					>
						Edit
					</Button>
				</div>
			</If>

			{/* Departure Trip Section */}
			<DepartureTripSection
				wizardData={wizardData}
				startLocation={startLocation}
				endLocation={endLocation}
				selectedDepartingTrip={selectedDepartingTrip}
				trips={trips.departureTickets}
				selectedTrips={selectedTrips}
				isStaff={isStaff}
				priceByFerryTrip={priceByDepartureFerryTrip}
				classPricesByFerryTrip={classPricesByFerryTrip}
				classPricesLoading={departureLayoutPending}
				pricesLoading={departurePriceLoading}
				tripsLoading={departureTripsLoading}
				refreshTrips={departureTripsQuery.refetch}
				currentTab={departingCurrentTab}
				setCurrentTab={setDepartingCurrentTab}
			/>

			{/* Return Trip Section */}
			<If condition={wizardData.tripType === 'return'}>
				<ReturnTripSection
					wizardData={wizardData}
					startLocation={startLocation}
					endLocation={endLocation}
					selectedReturningTrip={selectedReturningTrip}
					trips={trips.returningTickets}
					selectedTrips={selectedTrips}
					isStaff={isStaff}
					priceByFerryTrip={priceByReturnFerryTrip}
					classPricesByFerryTrip={classPricesByFerryTrip}
					classPricesLoading={returnLayoutPending}
					pricesLoading={destinationPriceLoading}
					tripsLoading={destinationTripsLoading}
					refreshTrips={returnTripsQuery.refetch}
					currentTab={returningCurrentTab}
					setCurrentTab={setReturningCurrentTab}
					onRemove={handleRemoveReturnTrip}
				/>
			</If>
		</div>
	);
}

// Helper components for cleaner JSX
function renderHeader(wizardData: BookingWizardData) {
	if (wizardData.tabSelected === 'freight') {
		return wizardData.wizardMode === 'CREATE' ? (
			<FormattedMessage
				id="booking-wizard__tickets__header__freight-select"
				defaultMessage="Select ferry tickets"
			/>
		) : (
			<FormattedMessage id="booking-wizard__tickets__header__freight-edit" defaultMessage="Edit ferry tickets" />
		);
	}

	if (wizardData.wizardMode === 'CREATE') {
		return (
			<FormattedMessage id="booking-wizard__tickets__header__select" defaultMessage="Select your ferry tickets" />
		);
	}

	if (wizardData.departureTrip !== undefined) {
		return wizardData.departureTrip ? (
			<FormattedMessage
				id="booking-wizard__tickets__header__edit-departing"
				defaultMessage="Edit your departing ferry ticket"
			/>
		) : (
			<FormattedMessage
				id="booking-wizard__tickets__header__edit-returning"
				defaultMessage="Edit your returning ferry ticket"
			/>
		);
	}

	return <FormattedMessage id="booking-wizard__tickets__header__edit" defaultMessage="Edit your ferry tickets" />;
}
